import React, { useState, useEffect, useMemo } from "react"
import {
    Box,
    TextField,
    Button,
    Grid,
    MenuItem,
    Typography,
    InputAdornment,
    Alert,
    Select,
    InputLabel,
    FormControl,
    Switch,
    FormControlLabel,
    FormHelperText
} from "@mui/material"
import { CreateFranchise, CreateFranchiseOwner } from "../../infrastructure/models/transport/request/createfranchise"
import { PaymentProvider } from "../../infrastructure/models/common/paymentProvider"

interface CreateFranchiseFormProps {
    onSubmit: (data: CreateFranchise) => void
    apiError?: string | null
}

const CreateFranchiseForm: React.FC<CreateFranchiseFormProps> = ({ onSubmit, apiError }) => {
    const [formData, setFormData] = useState<CreateFranchise>({
        name: "",
        status: "Active",
        address: {
            street: "",
            city: "",
            state: "",
            postalCode: "",
        },
        owner: {
            firstname: "",
            lastname: "",
            phone: { primary: "", secondary: "" },
            email: "",
            address: {
                street: "",
                city: "",
                state: "",
                postalCode: "",
            },
        },
        paymentInfo: {
            paymentBaseUrl: "",
            paymentUrl: "",
            merchantId: "",
            apiKey: "",
            businessUnitCode: "",
            statusNotifyUrl: "",
            frontendBackUrl: ""
        },
        paymentProvider: "" as PaymentProvider,
        excludeOrdersPercentage: undefined,
        paymentDeductionValue: undefined
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    /*  VALIDATIONS  */
    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.name) newErrors.name = "Franchise name is required"

        if (!formData.address.street)
            newErrors["address.street"] = "Street is required"
        if (!formData.address.city)
            newErrors["address.city"] = "City is required"
        if (!formData.address.state)
            newErrors["address.state"] = "State is required"
        if (!formData.address.postalCode)
            newErrors["address.postalCode"] = "Postal code is required"

        if (!formData.owner.firstname)
            newErrors["owner.firstname"] = "First name is required"
        if (!formData.owner.lastname)
            newErrors["owner.lastname"] = "Last name is required"
        if (!formData.owner.phone.primary)
            newErrors["owner.phone.primary"] = "Primary phone number is required"
        if (!formData.owner.email)
            newErrors["owner.email"] = "Email is required"

        if (!formData.owner.address.street)
            newErrors["owner.address.street"] = "Street is required"
        if (!formData.owner.address.city)
            newErrors["owner.address.city"] = "City is required"
        if (!formData.owner.address.state)
            newErrors["owner.address.state"] = "State is required"
        if (!formData.owner.address.postalCode)
            newErrors["owner.address.postalCode"] = "Postal code is required"

        if (!formData.paymentInfo.paymentBaseUrl)
            newErrors["paymentInfo.paymentBaseUrl"] =
                "Payment Base URL is required"
        if (!formData.paymentInfo.paymentUrl)
            newErrors["paymentInfo.paymentUrl"] = "Payment URL is required"
        if (!formData.paymentInfo.merchantId)
            newErrors["paymentInfo.merchantId"] = "Merchant ID is required"
        if (!formData.paymentInfo.apiKey)
            newErrors["paymentInfo.apiKey"] = "API Key is required"
        if (!formData.paymentInfo.businessUnitCode)
            newErrors["paymentInfo.businessUnitCode"] =
                "Business Unit Code is required"
        if (!formData.paymentInfo.frontendBackUrl)
            newErrors["paymentInfo.frontendBackUrl"] =
                "Frontend Back URL is required"
        if (!formData.paymentProvider)
            newErrors["paymentInfo.paymentProvider"] = "Payment Provider is required"

        if (formData.excludeOrdersPercentage) {
            if (formData.excludeOrdersPercentage === undefined || formData.excludeOrdersPercentage === null) {
                newErrors["excludeOrdersPercentage"] = "Percentage is required when Exclude Orders is enabled"
            } else if (formData.excludeOrdersPercentage < 1 || formData.excludeOrdersPercentage > 100) {
                newErrors["excludeOrdersPercentage"] = "Percentage must be between 1 and 100"
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target

        setErrors((prev) => ({ ...prev, [name]: "" }))

        setFormData((prev) => {
            const keys = name.split(".")

            if (keys.length === 1) {
                return { ...prev, [name]: value }
            }

            if (keys.length === 2) {
                return {
                    ...prev,
                    [keys[0]]: {
                        ...(prev[keys[0] as keyof CreateFranchise] as object),
                        [keys[1]]: value,
                    },
                }
            }

            if (keys.length === 3) {
                return {
                    ...prev,
                    [keys[0]]: {
                        ...(prev[keys[0] as keyof CreateFranchise] as object),
                        [keys[1]]: {
                            ...(prev[keys[0] as keyof CreateFranchise][
                                keys[1] as keyof CreateFranchiseOwner
                            ] as object),
                            [keys[2]]: value,
                        },
                    },
                }
            }

            if (name.startsWith("paymentInfo")) {
                const field = name.split(".")[1]
                return {
                    ...prev,
                    paymentInfo: { ...prev.paymentInfo, [field]: value },
                }
            }

            return prev
        })
    }

    const handleSubmit = () => {
        if (!validateForm()) return
        onSubmit(formData)
    }

    return (
        <Box
            sx={{
                p: 4,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
            }}
        >

            {apiError === "OWNER_ALREADY_EXISTS" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography fontWeight={600}>
                        Owner already exists
                    </Typography>
                    <Typography variant="body2">
                        An owner with the same <b>Email</b> or <b>Phone Number</b> is already active.
                        <br />
                        Please use a different Email or Phone Number.
                    </Typography>
                </Alert>
            )}

            {apiError && apiError !== "OWNER_ALREADY_EXISTS" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {apiError}
                </Alert>
            )}

            <Typography variant="h5" gutterBottom>
                Add Franchise
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Franchise Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 6 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        label="Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                    >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                        error={!!errors["address.street"]}
                        helperText={errors["address.street"]}
                    />
                </Grid>
                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        required
                        label="City"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                        error={!!errors["address.city"]}
                        helperText={errors["address.city"]}
                    />
                </Grid>
                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        required
                        label="State"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                        error={!!errors["address.state"]}
                        helperText={errors["address.state"]}
                    />
                </Grid>
                <Grid size={{ xs: 2 }}>
                    <TextField
                        fullWidth
                        required
                        label="Postal Code"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        slotProps={{
                            htmlInput: { maxLength: 6 }
                        }}
                        error={!!errors["address.postalCode"]}
                        helperText={errors["address.postalCode"]}
                    />
                </Grid>

                <Typography variant="h5" gutterBottom>
                    Owner Details
                </Typography>

                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner First Name"
                        name="owner.firstname"
                        value={formData.owner.firstname}
                        onChange={handleChange}
                        error={!!errors["owner.firstname"]}
                        helperText={errors["owner.firstname"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner Last Name"
                        name="owner.lastname"
                        value={formData.owner.lastname}
                        onChange={handleChange}
                        error={!!errors["owner.lastname"]}
                        helperText={errors["owner.lastname"]}
                    />
                </Grid>

                <Grid size={{ xs: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner Primary Number"
                        name="owner.phone"
                        value={formData.owner.phone.primary.replace("+91", "")}
                        onChange={(e) => {
                            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                owner: {
                                    ...prev.owner,
                                    phone: {
                                        ...prev.owner.phone,
                                        primary: `+91${onlyDigits}`
                                    }
                                }
                            }))

                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                        error={!!errors["owner.phone.primary"]}
                        helperText={errors["owner.phone.primary"]}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <TextField
                        fullWidth
                        label="Alternate Number"
                        name="owner.phone"
                        value={formData.owner.phone.secondary.replace("+91", "")}
                        onChange={(e) => {
                            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                owner: {
                                    ...prev.owner,
                                    phone: {
                                        ...prev.owner.phone,
                                        secondary: `+91${onlyDigits}`
                                    }
                                }
                            }))

                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner Email"
                        name="owner.email"
                        value={formData.owner.email}
                        onChange={handleChange}
                        error={!!errors["owner.email"]}
                        helperText={errors["owner.email"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner Street"
                        name="owner.address.street"
                        value={formData.owner.address.street}
                        onChange={handleChange}
                        error={!!errors["owner.address.street"]}
                        helperText={errors["owner.address.street"]}
                    />
                </Grid>
                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner City"
                        name="owner.address.city"
                        value={formData.owner.address.city}
                        onChange={handleChange}
                        error={!!errors["owner.address.city"]}
                        helperText={errors["owner.address.city"]}
                    />
                </Grid>
                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        required
                        label="Owner State"
                        name="owner.address.state"
                        value={formData.owner.address.state}
                        onChange={handleChange}
                        error={!!errors["owner.address.state"]}
                        helperText={errors["owner.address.state"]}
                    />
                </Grid>
                <Grid size={{ xs: 2 }}>
                    <TextField
                        fullWidth
                        required
                        label="Postal Code"
                        name="owner.address.postalCode"
                        value={formData.owner.address.postalCode}
                        onChange={handleChange}
                        slotProps={{
                            htmlInput: { maxLength: 6 }
                        }}
                        error={!!errors["owner.address.postalcode"]}
                        helperText={errors["owner.address.postalcode"]}
                    />
                </Grid>

                <Typography variant="h5" gutterBottom>
                    Payment Info
                </Typography>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Payment Base URL"
                        name="paymentInfo.paymentBaseUrl"
                        value={formData.paymentInfo.paymentBaseUrl}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.paymentBaseUrl"]}
                        helperText={errors["paymentInfo.paymentBaseUrl"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Payment URL"
                        name="paymentInfo.paymentUrl"
                        value={formData.paymentInfo.paymentUrl}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.paymentUrl"]}
                        helperText={errors["paymentInfo.paymentUrl"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Merchant ID"
                        name="paymentInfo.merchantId"
                        value={formData.paymentInfo.merchantId}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.merchantId"]}
                        helperText={errors["paymentInfo.merchantId"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="API Key"
                        name="paymentInfo.apiKey"
                        value={formData.paymentInfo.apiKey}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.apiKey"]}
                        helperText={errors["paymentInfo.apiKey"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Business Unit Code"
                        name="paymentInfo.businessUnitCode"
                        value={formData.paymentInfo.businessUnitCode}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.businessUnitCode"]}
                        helperText={errors["paymentInfo.businessUnitCode"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Status Notify URL"
                        name="paymentInfo.statusNotifyUrl"
                        value={formData.paymentInfo.statusNotifyUrl}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Frontend Back URL"
                        name="paymentInfo.frontendBackUrl"
                        value={formData.paymentInfo.frontendBackUrl}
                        onChange={handleChange}
                        error={!!errors["paymentInfo.frontendBackUrl"]}
                        helperText={errors["paymentInfo.frontendBackUrl"]}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth required error={!!errors["paymentInfo.paymentProvider"]}>
                        <InputLabel>Payment Provider</InputLabel>
                        <Select
                            label="Payment Provider"
                            name="paymentProvider"
                            value={formData.paymentProvider}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    paymentProvider: e.target.value as PaymentProvider,
                                }))
                            }
                        >
                            {Object.values(PaymentProvider).map((p) => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                        {errors["paymentInfo.paymentProvider"] && (
                            <Typography variant="caption" color="error">
                                {errors["paymentInfo.paymentProvider"]}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
                        Order Settings
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth  >
                        <InputLabel id="exclude-orders-percentage-label">
                            Exclude Orders Percentage (%)
                        </InputLabel>

                        <Select
                            labelId="exclude-orders-percentage-label"
                            label="Exclude Orders Percentage (%)"
                            name="excludeOrdersPercentage"
                            value={formData.excludeOrdersPercentage ?? 0}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    excludeOrdersPercentage: Number(e.target.value),
                                }))
                            }
                        >
                            {Array.from({ length: 101 }, (_, i) => (
                                <MenuItem key={i} value={i}>
                                    {i}%
                                </MenuItem>
                            ))}
                        </Select>

                        <FormHelperText>
                            {"Select the percentage of highest-value orders to exclude (0–100%)."}
                        </FormHelperText>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        select
                        fullWidth
                        label="Payment Deduction (%)"
                        value={formData.paymentDeductionValue ?? ""}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                paymentDeductionValue: e.target.value === "" ? undefined : e.target.value,
                            }))
                        }
                        helperText="Revenue deduction % (1–100). Defaults to system setting if not set."
                    >
                        <MenuItem value="">Default (system setting)</MenuItem>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => (
                            <MenuItem key={n} value={String(n)}>{n}%</MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Box>
    )
}

export default CreateFranchiseForm